import { useCallback, useMemo } from 'react'

import { CursorState } from '@editor/config/constants/cursor'
import { CircleAnchorProps } from '@editor/interfaces/canvas'
import { BlueprintEditorTools } from '@editor/pages/projects/blueprint-editor/definitions'
import { RootState, useAppDispatch } from '@editor/store/app'
import { setCursor } from '@editor/store/tool'
import { CircleAnchorIcon } from '@infraloop/common'
import { useSelector } from 'react-redux'

import { setIsClosing } from '../store'

const useAnchors = () => {
  // Store
  const dispatch = useAppDispatch()
  const workingPoints = useSelector((root: RootState) => root.bpToolPolygon.workingPoints)
  const selectedTool = useSelector((state: RootState) => state.blueprint.editor.selectedTool)
  const isClosing = useSelector((state: RootState) => state.bpToolPolygon.isClosing)

  // Vars
  const isSelectedTool = useMemo(() => selectedTool === BlueprintEditorTools.Polygon, [selectedTool])

  /**
   * Get tooltip to be used for the first anchor.
   */
  const getFirstAnchorTooltip = useCallback(() => {
    if (workingPoints.length < 2) return undefined

    return workingPoints.length > 2
      ? 'この平面の作成を完了'
      : `この平面の作成を完了するには、あと${3 - workingPoints.length}点以上が必要です`
  }, [workingPoints])

  /**
   * Setup working point anchors
   */
  const workingPointAnchors = useMemo(() => {
    if (!isSelectedTool) return []

    const anchors: CircleAnchorProps[] = []

    // During drawing, first anchor is always grey until there are more than 3 points
    if (workingPoints.length) {
      let style: Partial<CircleAnchorProps> = {
        scale: 1,
        color: '#777',
      }

      if (workingPoints.length >= 3) {
        style = {
          color: '#248CE4',
          icon: CircleAnchorIcon.CHECK,
          iconColor: 'white',
          outlineOpacity: 0,
          scale: 1.3,
          iconScale: 1.1,
        }
      }

      anchors.push({
        ...style,
        id: 'polygon-working-points-first',
        point: workingPoints[0],
        tooltipText: getFirstAnchorTooltip(),
        onEnter: () => {
          if (workingPoints.length > 2) {
            dispatch(setCursor(CursorState.POINTER))
            dispatch(setIsClosing(true))
          }
        },
        onLeave: () => {
          if (isClosing) {
            dispatch(setIsClosing(false))
            dispatch(setCursor(CursorState.DEFAULT))
          }
        },
      })
    }

    return anchors
  }, [workingPoints, isClosing, isSelectedTool, dispatch, getFirstAnchorTooltip])

  return {
    anchors: [workingPointAnchors].flat(),
  }
}

export default useAnchors
